<template>
  <div style="position: relative;">
    <a-page-header :title="`执行记录详情 - ${logDetail?.step_name || ''} - ${logDetail?.step_order || ''}.${logDetail?.sub_step_order || ''}`">
      <template #extra>
        <a-button @click="handleBack">返回</a-button>
      </template>
    </a-page-header>

    <!-- 右侧浮动导航 -->
    <a-affix :offsetTop="100" style="position: fixed; right: 20px; top: 100px; z-index: 100;">
      <a-card size="small" style="width: 200px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
        <template #title>
          <span style="font-size: 14px; font-weight: 500;">导航</span>
        </template>
        <a-anchor :affix="false" :offsetTop="100">
          <a-anchor-link href="#basic-info" title="基本信息" />
          <a-anchor-link v-if="knowledgeList.length > 0" href="#knowledge_ids" title="关联的知识列表" />
          <a-anchor-link v-if="executedKnowledgeList.length > 0" href="#executed_knowledge_ids" title="已执行的知识列表" />
          <a-anchor-link v-if="logDetail?.request_data" href="#request_data" title="车辆数据汇总" />
          <a-anchor-link v-if="logDetail?.ai_request_content" href="#ai_request_content" title="AI请求内容" />
          <a-anchor-link v-if="logDetail?.response_data" href="#response_data" title="响应数据" />
          <a-anchor-link v-if="logDetail?.ai_raw_response" href="#ai_raw_response" title="原始AI响应" />
          <a-anchor-link v-if="logDetail?.error_message" href="#error_message" title="错误信息" />
        </a-anchor>
      </a-card>
    </a-affix>

    <a-spin :spinning="loading" style="width: 100%; margin-top: 20px;">
      <a-card style="margin-top: 16px; margin-right: 240px;" v-if="logDetail">
        <!-- 顶部关键信息 -->
        <div id="basic-info" style="scroll-margin-top: 100px;">
          <a-descriptions :column="3" bordered style="margin-bottom: 24px">
          <a-descriptions-item label="步骤名称">{{ logDetail.step_name }}</a-descriptions-item>
          <a-descriptions-item label="执行状态">
            <a-tag :color="getStatusColor(logDetail.status)">
              {{ getStatusText(logDetail.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="开始时间">{{ formatTime(logDetail.start_time) }}</a-descriptions-item>
          <a-descriptions-item label="结束时间">{{ formatTime(logDetail.end_time) }}</a-descriptions-item>
          <a-descriptions-item label="执行耗时">{{ formatDuration(logDetail.duration) }}</a-descriptions-item>
          <a-descriptions-item label="关联的知识数量">{{ logDetail.knowledge_count || 0 }}</a-descriptions-item>
          </a-descriptions>
        </div>

        <!-- 长内容字段（折叠面板） -->
        <a-collapse v-model:activeKey="activeKeys" style="margin-bottom: 16px">
          <!-- 关联的知识列表 -->
          <a-collapse-panel key="knowledge_ids" header="关联的知识列表" v-if="knowledgeList.length > 0 || promptStructureInfo || outputParamsContent">
            <div id="knowledge_ids" style="scroll-margin-top: 100px;">
              <!-- 提示词结构名称（在知识列表上面） -->
              <div v-if="promptStructureInfo" style="margin-bottom: 16px;">
                <div style="font-weight: 500; margin-bottom: 8px;">提示词结构名称：</div>
                <a 
                  :href="getKnowledgeUrl(promptStructureInfo.id)" 
                  target="_blank"
                  class="knowledge-link"
                >
                  {{ promptStructureInfo.title || promptStructureInfo.name || '未知名称' }}({{ promptStructureInfo.id }})
                </a>
              </div>
              
              <!-- 关联的知识列表 -->
              <div v-if="knowledgeList.length > 0" style="margin-bottom: 16px;">
                <div style="font-weight: 500; margin-bottom: 8px;">关联的知识列表：</div>
                <div style="margin-bottom: 8px;">
                  <a 
                    v-for="(item, index) in knowledgeList" 
                    :key="index" 
                    :href="getKnowledgeUrl(item.id)" 
                    target="_blank"
                    class="knowledge-link"
                    style="display: inline-block; margin: 4px 8px 4px 0;"
                  >
                    [{{ item.type_name || '未知类型' }}] {{ item.title || '未知名称' }}({{ item.id }})
                  </a>
                </div>
              </div>
              
              <!-- 出参定义字段（在知识列表下面） -->
              <div v-if="outputParamsContent" style="margin-top: 16px;">
                <div style="font-weight: 500; margin-bottom: 8px;">出参定义字段：</div>
                <div style="background-color: #f6f8fa; padding: 12px; border-radius: 4px; font-family: monospace; font-size: 12px; border: 1px solid #e1e4e8;">
                  <pre ref="outputParamsRef" class="code-block" style="margin: 0; white-space: pre-wrap; word-wrap: break-word;"></pre>
                </div>
              </div>
            </div>
          </a-collapse-panel>

          <!-- 已执行的知识列表 -->
          <a-collapse-panel key="executed_knowledge_ids" header="已执行的知识列表" v-if="executedKnowledgeList.length > 0">
            <div id="executed_knowledge_ids" style="scroll-margin-top: 100px;">
            <div style="margin-bottom: 8px;">
              <a-tag v-for="(item, index) in executedKnowledgeList" :key="index" style="margin: 4px;">
                [{{ item.type_name || '未知类型' }}] {{ item.title || '未知名称' }}({{ item.id }})
              </a-tag>
            </div>
            </div>
          </a-collapse-panel>

          <!-- 车辆数据汇总 -->
          <a-collapse-panel key="request_data" header="车辆数据汇总" v-if="logDetail.request_data">
            <div id="request_data" style="scroll-margin-top: 100px;">
              <div v-for="group in vehicleInfoGroups" :key="group.label" style="margin-bottom: 20px;">
                <div style="font-weight: 600; font-size: 13px; color: #595959; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #f0f0f0;">
                  {{ group.label }}
                </div>
                <table class="vi-table">
                  <colgroup>
                    <col style="width: 260px;" />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr v-for="item in group.items" :key="item.key">
                      <td class="vi-table__label">
                        <div>{{ item.label }}</div>
                        <div class="vi-table__field">{{ item.key }}</div>
                      </td>
                      <td class="vi-table__value">
                        <template v-if="item.isEmpty">
                          <span class="vi-table__empty">-</span>
                        </template>
                        <template v-else-if="item.isComplex">
                          <pre class="code-block code-block--inline" v-html="item.highlighted"></pre>
                        </template>
                        <template v-else>
                          <div class="vi-table__text">{{ item.value }}</div>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </a-collapse-panel>

          <!-- AI请求内容 -->
          <a-collapse-panel key="ai_request_content" header="AI请求内容" v-if="logDetail.ai_request_content">
            <div id="ai_request_content" style="scroll-margin-top: 100px;">
            <div style="position: relative;">
              <a-button 
                type="text" 
                size="small" 
                style="position: absolute; right: 0; top: 0; z-index: 10;"
                @click="handleCopy(logDetail.ai_request_content)"
              >
                <template #icon><CopyOutlined /></template>
                复制
              </a-button>
              <pre ref="aiRequestContentRef" class="code-block"></pre>
            </div>
            </div>
          </a-collapse-panel>

          <!-- 响应数据 -->
          <a-collapse-panel key="response_data" header="响应数据" v-if="logDetail.response_data">
            <div id="response_data" style="scroll-margin-top: 100px;">
            <div style="position: relative;">
              <a-button 
                type="text" 
                size="small" 
                style="position: absolute; right: 0; top: 0; z-index: 10;"
                @click="handleCopy(logDetail.response_data)"
              >
                <template #icon><CopyOutlined /></template>
                复制
              </a-button>
              <pre ref="responseDataRef" class="code-block"></pre>
            </div>
            </div>
          </a-collapse-panel>

          <!-- 原始AI响应 -->
          <a-collapse-panel key="ai_raw_response" header="原始AI响应" v-if="logDetail.ai_raw_response">
            <div id="ai_raw_response" style="scroll-margin-top: 100px;">
            <div style="position: relative;">
              <a-button 
                type="text" 
                size="small" 
                style="position: absolute; right: 0; top: 0; z-index: 10;"
                @click="handleCopy(logDetail.ai_raw_response)"
              >
                <template #icon><CopyOutlined /></template>
                复制
              </a-button>
              <pre ref="aiRawResponseRef" class="code-block"></pre>
            </div>
            </div>
          </a-collapse-panel>

          <!-- 错误信息 -->
          <a-collapse-panel key="error_message" header="错误信息" v-if="logDetail.error_message">
            <div id="error_message" style="scroll-margin-top: 100px;">
              <a-alert :message="logDetail.error_message" type="error" />
            </div>
          </a-collapse-panel>
        </a-collapse>
      </a-card>

      <div v-else style="text-align: center; padding: 50px;">
        <a-empty description="加载中..." />
      </div>
    </a-spin>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { CopyOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import { getExecutionLogById } from '@/api/executionLogs';
import { batchGetKnowledge, getKnowledgeById } from '@/api/knowledge';
import { getCallStepById } from '@/api/callStep';

export default {
  name: 'ExecutionLogDetail',
  components: {
    CopyOutlined
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(false);
    const logDetail = ref(null);
    const knowledgeList = ref([]);
    const executedKnowledgeList = ref([]);
    const activeKeys = ref([]);
    const promptStructureInfo = ref(null);
    const outputParamsContent = ref(null);
    
    // 代码高亮ref
    const aiRequestContentRef = ref(null);
    const responseDataRef = ref(null);
    const aiRawResponseRef = ref(null);
    const outputParamsRef = ref(null);

    const getStatusColor = (status) => {
      const colors = {
        pending: 'orange',
        executing: 'blue',
        success: 'green',
        failed: 'red',
        skipped: 'default'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        success: '成功',
        failed: '失败',
        skipped: '跳过'
      };
      return texts[status] || status;
    };

    const formatTime = (time) => {
      if (!time) return '-';
      return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
    };

    const formatDuration = (duration) => {
      if (!duration) return '-';
      const seconds = (duration / 1000).toFixed(1);
      return `${seconds}s`;
    };

    // 将 vehicleInfo 字段分组展示
    const vehicleInfoGroups = computed(() => {
      const vi = logDetail.value?.request_data?.vehicle_info;
      if (!vi) return [];

      const highlight = (val) => {
        try {
          const str = JSON.stringify(val, null, 2);
          return hljs.highlight(str, { language: 'json' }).value;
        } catch {
          return String(val);
        }
      };

      const makeItem = (key, label, val) => {
        const isEmpty = val === null || val === undefined || val === '';
        const isComplex = !isEmpty && typeof val === 'object';
        return {
          key,
          label,
          isEmpty,
          value: (!isEmpty && !isComplex) ? String(val) : null,
          isComplex,
          highlighted: isComplex ? highlight(val) : null,
        };
      };

      const group = (label, pairs) => {
        const items = pairs.map(([k, l, v]) => makeItem(k, l, v));
        return { label, items };
      };

      const meta = logDetail.value?.request_data?._metadata;

      const KNOWN_KEYS = new Set([
        'task_id', 'plan_id',
        'engines', 'system_voltage', 'fault_phenomenon', 'user_additional_info',
        'controlUnits',
        'subsystems', 'components',
        'engine_info', 'fault_code_list', 'ecu_version_info_list',
        'dcu_info', 'dcu_version', 'dcu_fault_codes',
        'stream_list', 'selected_streams', 'csv_data_stream',
        'working_conditions', 'working_hours', 'condition_name',
      ]);

      const unknownItems = Object.keys(vi)
        .filter(k => !KNOWN_KEYS.has(k))
        .map(k => makeItem(k, k, vi[k]));

      const groups = [
        group('基础标识', [
          ['task_id', '任务 ID', vi.task_id],
          ['plan_id', '执行计划 ID', vi.plan_id],
        ]),
        group('车辆信息', [
          ['engines', '发动机', vi.engines],
          ['system_voltage', '系统电压', vi.system_voltage],
          ['fault_phenomenon', '故障现象', vi.fault_phenomenon],
          ['user_additional_info', '用户补充说明', vi.user_additional_info],
        ]),
        group('控制单元', [
          ['controlUnits', '控制单元', vi.controlUnits],
        ]),
        group('子系统 / 部件', [
          ['subsystems', '子系统', vi.subsystems],
          ['components', '部件', vi.components],
        ]),
        group('ECU 诊断', [
          ['engine_info', 'ECU 系统信息', vi.engine_info],
          ['fault_code_list', 'ECU 故障码', vi.fault_code_list],
          ['ecu_version_info_list', 'ECU 版本信息', vi.ecu_version_info_list],
        ]),
        group('DCU 诊断', [
          ['dcu_info', 'DCU 系统信息', vi.dcu_info],
          ['dcu_version', 'DCU 版本信息', vi.dcu_version],
          ['dcu_fault_codes', 'DCU 故障码', vi.dcu_fault_codes],
        ]),
        group('数据流', [
          ['stream_list', '支持的数据流', vi.stream_list],
          ['selected_streams', '选中的数据流', vi.selected_streams],
          ['csv_data_stream', 'CSV 采集数据', vi.csv_data_stream],
        ]),
        group('工况', [
          ['working_conditions', '工况定义', vi.working_conditions],
          ['working_hours', '工况时长', vi.working_hours],
          ['condition_name', '工况名称', vi.condition_name],
        ]),
        group('执行元数据', [
          ['inputTokenCount', '输入 Token', meta?.inputTokenCount],
          ['outputTokenCount', '输出 Token', meta?.outputTokenCount],
          ['modelId', '使用模型', meta?.modelId],
        ]),
      ];

      if (unknownItems.length > 0) {
        groups.push({ label: '其他数据', items: unknownItems });
      }

      return groups;
    });

    const handleCopy = async (text) => {
      try {
        if (typeof text === 'object') {
          text = JSON.stringify(text, null, 2);
        }
        await navigator.clipboard.writeText(text);
        message.success('已复制到剪贴板');
      } catch (error) {
        message.error('复制失败');
        console.error(error);
      }
    };

    const highlightCode = (element, content, language = 'json') => {
      if (!element || !content) return;
      
      try {
        let formattedContent = content;
        
        // 如果是JSON，尝试格式化
        if (language === 'json') {
          try {
            const parsed = typeof content === 'string' ? JSON.parse(content) : content;
            formattedContent = JSON.stringify(parsed, null, 2);
          } catch (e) {
            // 如果不是有效的JSON，使用原始内容
            formattedContent = typeof content === 'string' ? content : JSON.stringify(content);
          }
        }
        
        // 高亮代码
        element.innerHTML = hljs.highlight(formattedContent, { language }).value;
      } catch (error) {
        // 如果高亮失败，直接显示原始内容
        element.textContent = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
      }
    };

    const loadKnowledgeDetails = async (knowledgeIds) => {
      if (!knowledgeIds || knowledgeIds.length === 0) return [];
      
      try {
        // 解析JSON字符串
        const ids = typeof knowledgeIds === 'string' ? JSON.parse(knowledgeIds) : knowledgeIds;
        if (!Array.isArray(ids) || ids.length === 0) return [];
        
        const res = await batchGetKnowledge(ids);
        if (res.code === 200) {
          return res.data.filter(item => item !== null);
        }
        return [];
      } catch (error) {
        console.error('加载知识详情失败:', error);
        return [];
      }
    };

    const loadLogDetail = async () => {
      loading.value = true;
      try {
        const logId = route.params.id;
        const res = await getExecutionLogById(logId);
        if (res.code === 200) {
          logDetail.value = res.data;
          
          // 加载知识详情
          if (logDetail.value.knowledge_ids) {
            knowledgeList.value = await loadKnowledgeDetails(logDetail.value.knowledge_ids);
          }
          if (logDetail.value.executed_knowledge_ids) {
            executedKnowledgeList.value = await loadKnowledgeDetails(logDetail.value.executed_knowledge_ids);
          }
          
          // 加载提示词结构和出参定义信息
          await loadPromptStructureAndOutputParams();
          
          // 默认展开所有面板
          activeKeys.value = [
            'knowledge_ids',
            'executed_knowledge_ids',
            'request_data',
            'ai_request_content',
            'response_data',
            'ai_raw_response',
            'error_message'
          ].filter(key => {
            if (key === 'knowledge_ids') return knowledgeList.value.length > 0;
            if (key === 'executed_knowledge_ids') return executedKnowledgeList.value.length > 0;
            if (key === 'request_data') return logDetail.value.request_data;
            if (key === 'ai_request_content') return logDetail.value.ai_request_content;
            if (key === 'response_data') return logDetail.value.response_data;
            if (key === 'ai_raw_response') return logDetail.value.ai_raw_response;
            if (key === 'error_message') return logDetail.value.error_message;
            return false;
          });
          
          // 等待DOM更新后高亮代码
          await nextTick();
          highlightCodeBlocks();
        } else {
          message.error(res.message || '加载记录详情失败');
        }
      } catch (error) {
        message.error('加载记录详情失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const highlightCodeBlocks = () => {
      // 高亮AI请求内容（XML）
      if (aiRequestContentRef.value && logDetail.value.ai_request_content) {
        highlightCode(aiRequestContentRef.value, logDetail.value.ai_request_content, 'xml');
      }
      
      // 高亮响应数据
      if (responseDataRef.value && logDetail.value.response_data) {
        highlightCode(responseDataRef.value, logDetail.value.response_data, 'json');
      }
      
      // 高亮原始AI响应（文本）
      if (aiRawResponseRef.value && logDetail.value.ai_raw_response) {
        highlightCode(aiRawResponseRef.value, logDetail.value.ai_raw_response, 'plaintext');
      }
      
      // 高亮出参定义字段（JSON）
      if (outputParamsRef.value && outputParamsContent.value) {
        highlightCode(outputParamsRef.value, outputParamsContent.value, 'json');
      }
    };

    // 监听折叠面板展开，重新高亮代码
    watch(activeKeys, () => {
      nextTick(() => {
        highlightCodeBlocks();
      });
    });

    const handleBack = () => {
      window.close();
    };

    // 获取知识详情页URL
    const getKnowledgeUrl = (knowledgeId) => {
      const routeData = router.resolve({
        name: 'knowledge-edit',
        params: { id: knowledgeId }
      });
      return routeData.href;
    };

    // 加载提示词结构和出参定义信息
    const loadPromptStructureAndOutputParams = async () => {
      if (!logDetail.value?.step_id) return;
      
      try {
        // 通过 step_id 获取 worker 信息
        const stepRes = await getCallStepById(logDetail.value.step_id);
        if (stepRes.code === 200 && stepRes.data) {
          const stepData = stepRes.data;
          
          // 加载提示词结构信息（prompt_template_id）
          if (stepData.prompt_template_id) {
            const promptRes = await getKnowledgeById(stepData.prompt_template_id);
            if (promptRes.code === 200 && promptRes.data) {
              promptStructureInfo.value = promptRes.data;
            }
          }
          
          // 加载出参定义信息（直接显示 output_params 的 JSON 内容）
          if (stepData.output_params) {
            try {
              const outputParams = typeof stepData.output_params === 'string' 
                ? JSON.parse(stepData.output_params) 
                : stepData.output_params;
              
              // 格式化为 JSON 字符串
              outputParamsContent.value = JSON.stringify(outputParams, null, 2);
            } catch (e) {
              console.warn('解析 output_params 失败:', e);
              // 如果解析失败，显示原始内容
              outputParamsContent.value = typeof stepData.output_params === 'string' 
                ? stepData.output_params 
                : JSON.stringify(stepData.output_params, null, 2);
            }
          }
        }
      } catch (error) {
        console.error('加载提示词结构和出参定义失败:', error);
      }
    };

    onMounted(() => {
      loadLogDetail();
    });

    return {
      loading,
      logDetail,
      knowledgeList,
      executedKnowledgeList,
      activeKeys,
      aiRequestContentRef,
      responseDataRef,
      aiRawResponseRef,
      outputParamsRef,
      getStatusColor,
      getStatusText,
      formatTime,
      formatDuration,
      handleCopy,
      handleBack,
      vehicleInfoGroups,
      promptStructureInfo,
      outputParamsContent,
      getKnowledgeUrl
    };
  }
};
</script>

<style scoped>
.code-block {
  background-color: #f6f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 3px;
  padding: 16px;
  overflow-x: auto;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 12px;
  line-height: 1.45;
  margin: 0;
}

.code-block--inline {
  padding: 8px 12px;
  max-height: 300px;
  overflow-y: auto;
}

.vi-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.vi-table__label {
  width: 260px;
  min-width: 260px;
  max-width: 260px;
  padding: 8px 12px;
  vertical-align: middle;
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
  line-height: 1.5;
}

.vi-table__field {
  font-size: 11px;
  color: #8c8c8c;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  margin-top: 2px;
}

.vi-table__value {
  padding: 8px 12px;
  vertical-align: middle;
  border: 1px solid #f0f0f0;
  word-break: break-all;
}

.vi-table__text {
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  line-height: 1.5;
}

.vi-table__empty {
  color: #bfbfbf;
}

.code-block::-webkit-scrollbar {
  height: 8px;
}

.code-block::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.code-block::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.code-block::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.knowledge-link {
  color: #1890ff;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.3s;
}

.knowledge-link:hover {
  color: #40a9ff;
  text-decoration: underline;
}

.knowledge-link:active {
  color: #096dd9;
}
</style>
